export function normalizeEpisodeConfig(config = {}) {
	return {
		...config,
		fields: Array.isArray(config.fields) ? config.fields.map(field => ({...field, options: Array.isArray(field.options) ? field.options : []})) : [],
		archivedFields: Array.isArray(config.archivedFields) ? config.archivedFields : [],
		ratingScaleMin: config.ratingScaleMin === undefined ? 0 : Number(config.ratingScaleMin),
		ratingScaleMax: Number(config.ratingScaleMax) || 100,
	}
}

export function createEpisodePayload(payload = {}, config = {}, entries = [], {includeEmpty = false} = {}) {
	return {...payload, tracker: normalizeEpisodeConfig(config), entries: includeEmpty ? entries : normalizeEpisodeEntries(entries)}
}

export function summarizeEpisodes(entries = [], config = {}) {
	let minutesFields = (config.fields || []).filter(field => field.type === "minutes" && field.id === config.durationFieldId)
	if (!minutesFields.length) return {episodeCount: entries.length, reportedMinutes: null, missingMinutes: 0}
	let minutes = 0
	let missingMinutes = 0
	for (let entry of entries) {
		for (let field of minutesFields) {
			let value = entry.answers?.[field.id]
			if (value === null || value === undefined || value === "") {
				missingMinutes++
			} else if (Number.isFinite(Number(value))) {
				minutes += Number(value)
			}
		}
	}
	return {episodeCount: entries.length, reportedMinutes: minutes, missingMinutes}
}

export function formatEpisodeDate(value) {
	if (!value) return ""
	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? "" : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

export function sortEpisodeEntries(entries, config, sortBy = "chronological") {
	const dateId = config.fields.find(field => field.type === "date")?.id
	const timeId = config.fields.find(field => field.type === "time")?.id
	const field = config.fields.find(field => field.id === sortBy)
	const numeric = field && ["rating", "minutes"].includes(field.type)
	const valueFor = entry => sortBy === "chronological"
		? `${entry.answers?.[dateId] || "9999-99-99"} ${entry.answers?.[timeId] || "99:99"}`
		: entry.answers?.[sortBy]
	return [...entries].sort((left, right) => {
		const a = valueFor(left)
		const b = valueFor(right)
		const missingA = a === null || a === undefined || a === ""
		const missingB = b === null || b === undefined || b === ""
		if (missingA || missingB) return Number(missingA) - Number(missingB)
		return numeric ? Number(a) - Number(b) : String(a).localeCompare(String(b), undefined, {numeric: true})
	})
}

export function groupEpisodeEntries(entries, groupings = {}) {
	const labelFor = entry => String(groupings[entry.id] || "").trim()
	if (!entries.some(entry => labelFor(entry))) return [{label: "", entries}]
	const groups = new Map()
	for (const entry of entries) {
		const label = labelFor(entry)
		if (!groups.has(label)) groups.set(label, [])
		groups.get(label).push(entry)
	}
	return [...groups].map(([label, groupedEntries]) => ({label: label || "Ungrouped", entries: groupedEntries}))
}

export function getEpisodeConfigErrors(config = {}) {
	let validTypes = new Set(["date", "time", "text", "multiline", "rating", "minutes", "checkbox"])
	if (!config.title || !config.entryLabel || !Array.isArray(config.fields)) {
		return ["Invalid episode tracker configuration"]
	}
	let ids = new Set()
	for (let field of config.fields) {
		if (!field.id || !field.title || !validTypes.has(field.type) || ids.has(field.id)) {
			return ["Invalid episode tracker field"]
		}
		ids.add(field.id)
		if (field.type === "checkbox" && (!Array.isArray(field.options) || field.options.some(option => !option.id || !option.title))) {
			return ["Invalid episode tracker options"]
		}
	}
	const ratingMin = config.ratingScaleMin === undefined ? 0 : Number(config.ratingScaleMin)
	if (config.fields.some(field => field.type === "rating") && (!Number.isInteger(ratingMin) || ratingMin < 0 || ratingMin > 99 || !Number.isInteger(Number(config.ratingScaleMax)) || Number(config.ratingScaleMax) <= ratingMin || Number(config.ratingScaleMax) > 100)) {
		return ["Invalid episode tracker rating range"]
	}
	return []
}

export function getEpisodeEntryErrors(entries = [], tracker = {}) {
	for (let entry of entries) {
		if (!entry.id || !entry.answers || typeof entry.answers !== "object") {
			return ["Invalid episode entry"]
		}
		for (let field of tracker.fields || []) {
			let value = entry.answers[field.id]
			if (value === null || value === undefined || value === "") continue
			if (field.type === "rating" && (!Number.isInteger(Number(value)) || Number(value) < Number(tracker.ratingScaleMin ?? 0) || Number(value) > Number(tracker.ratingScaleMax))) {
				return ["Invalid episode rating"]
			}
			if (field.type === "minutes" && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
				return ["Invalid episode minutes"]
			}
			if (field.type === "checkbox" && (!Array.isArray(value) || value.some(id => ![...(field.options || []), ...(field.archivedOptions || [])].some(option => option.id === id)))) {
				return ["Invalid episode choice"]
			}
		}
	}
	return []
}

export function normalizeEpisodeEntries(entries = []) {
	return (Array.isArray(entries) ? entries : []).filter(entry => Object.values(entry?.answers || {}).some(value =>
		value !== null && value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0)
	))
}
